/**
 * Compresses a photo by resizing it to fit within a maximum dimension and converting it to a WebP blob.
 * @function CompressPhoto
 * @param {File} photo - The source photo to compress.
 * @param {Object} [options] - Compression options.
 * @param {number} [options.maxDimension] - The maximum width or height (in pixels) for the output image. If not provided, the original dimensions are used.
 * @param {number} [options.quality=0.75] - The quality of the output WebP image (between 0 and 1).
 * @returns {Promise<Blob>} A promise that resolves to a compressed WebP image blob.
 */
export async function CompressPhoto(photo, options) {
  const maxDimension = options?.maxDimension ?? Infinity;
  const quality = options?.quality ?? 0.75;

  // Prepare the canvas and context
  const canvas = new OffscreenCanvas(1, 1);
  const context = canvas.getContext('2d');
  const drawable = await createImageBitmap(photo);
  const { height: originalHeight, width: originalWidth } = drawable;

  // Shrink the image if it exceeds the given maximum dimension
  const largestDimension = Math.max(originalWidth, originalHeight);
  const { height: imageHeight, width: imageWidth } =
    largestDimension > maxDimension ? GetAdjustedDimensions(originalWidth, originalHeight, maxDimension) : drawable;

  // draw the image to the canvas
  canvas.width = imageWidth;
  canvas.height = imageHeight;
  context?.drawImage(drawable, 0, 0, imageWidth, imageHeight);
  drawable.close();

  // convert to a webp blob
  const webPBlob = await canvas.convertToBlob({ type: 'image/webp', quality });
  return webPBlob;
}

/**
 * Calculates the dimensions for an image while preserving its aspect ratio,
 * ensuring that the largest dimension does not exceed the specified maxDimension.
 * @param {number} originalPhotoWidth - The original width of the photo.
 * @param {number} originalPhotoHeight - The original height of the photo.
 * @param {number} maxDimension - The maximum allowed size for the largest dimension (width or height).
 * @returns {{ width: number, height: number }} The new scaled width and height of the image.
 */
function GetAdjustedDimensions(originalPhotoWidth, originalPhotoHeight, maxDimension) {
  let newPhotoWidth, newPhotoHeight;
  if (originalPhotoWidth > originalPhotoHeight) {
    newPhotoWidth = maxDimension;
    newPhotoHeight = Math.floor((originalPhotoHeight * newPhotoWidth) / originalPhotoWidth);
  } else {
    newPhotoHeight = maxDimension;
    newPhotoWidth = Math.floor((originalPhotoWidth * newPhotoHeight) / originalPhotoHeight);
  }
  return { width: newPhotoWidth, height: newPhotoHeight };
}
